<script setup>
import { Dialog as DialogParent, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue';
import { useDialogStore } from '@/stores/dialogStore';
import { IconAlertTriangle, IconCheck, IconInfoCircle, IconX } from '@tabler/icons-vue';
import BaseButton from '@/components/base/BaseButton.vue';
import { computed } from 'vue';
import { DialogVariants } from '@/constants/dialog';

const dialog = useDialogStore();

const variantConfig = {
    [DialogVariants.Primary]: { icon: IconCheck, iconColor: 'text-green-600', bg: 'bg-green-100' },
    [DialogVariants.Danger]: { icon: IconAlertTriangle, iconColor: 'text-red-600', bg: 'bg-red-100' },
    [DialogVariants.Info]: { icon: IconInfoCircle, iconColor: 'text-blue-600', bg: 'bg-blue-100' },
};

const currentVariant = computed(() => variantConfig[dialog.variant] ?? variantConfig[DialogVariants.Info]);
const iconComponent = computed(() => currentVariant.value.icon);
const iconColorClass = computed(() => currentVariant.value.iconColor);
const backgroundColorClass = computed(() => currentVariant.value.bg);

function resolveDialog(resValue) {
    dialog.confirm(resValue);
}
</script>

<template>
    <Teleport to="body">
        <TransitionRoot
            as="template"
            :show="dialog.active"
        >
            <DialogParent
                as="div"
                class="relative z-90"
                @close="dialog.close"
            >
                <TransitionChild
                    as="template"
                    enter="ease-out duration-300"
                    enter-from="opacity-0"
                    enter-to="opacity-100"
                    leave="ease-in duration-200"
                    leave-from="opacity-100"
                    leave-to="opacity-0"
                >
                    <div class="fixed inset-0 bg-black-100/60 transition-opacity" />
                </TransitionChild>

                <div class="fixed inset-0 z-90 w-screen overflow-y-auto">
                    <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                        <TransitionChild
                            as="template"
                            enter="ease-out duration-300"
                            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enter-to="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leave-from="opacity-100 translate-y-0 sm:scale-100"
                            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <DialogPanel
                                class="relative inline-block w-full transform rounded-lg bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:max-w-xl sm:align-middle"
                            >
                                <div class="absolute right-0 top-0 pr-4 pt-4">
                                    <button
                                        type="button"
                                        class="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                                        @click="resolveDialog(false)"
                                    >
                                        <span class="sr-only">Close</span>
                                        <IconX
                                            class="size-6"
                                            aria-hidden="true"
                                        />
                                    </button>
                                </div>

                                <div class="flex p-6 gap-6">
                                    <div
                                        class="shrink-0 flex size-12 items-center justify-center rounded-full"
                                        :class="backgroundColorClass"
                                    >
                                        <component
                                            :is="iconComponent"
                                            :class="iconColorClass"
                                            class="size-6"
                                            aria-hidden="true"
                                        />
                                    </div>

                                    <div class="grow">
                                        <DialogTitle
                                            as="h3"
                                            class="mt-1 mb-3 text-xl leading-6 font-semibold text-black-900"
                                        >
                                            {{ dialog.title }}
                                        </DialogTitle>
                                        <div class="text-sm text-black-500">
                                            {{ dialog.message }}
                                        </div>
                                    </div>
                                </div>

                                <div class="flex flex-row-reverse items-center gap-x-4 rounded-b-lg bg-gray-50 px-6 py-3">
                                    <template v-if="!dialog.hideNoButton">
                                        <BaseButton
                                            type="button"
                                            :label="dialog.actionText"
                                            :variant="dialog.variant === DialogVariants.Danger ? 'danger' : 'primary'"
                                            :icon="dialog.actionIcon"
                                            :loading="dialog.loading"
                                            :disabled="dialog.loading"
                                            class="col-start-2 w-full sm:w-auto"
                                            @click="resolveDialog(true)"
                                        />

                                        <BaseButton
                                            variant="default"
                                            :label="dialog.cancelText"
                                            type="button"
                                            :icon="dialog.cancelIcon"
                                            :disabled="dialog.loading"
                                            class="col-start-1 w-full sm:w-auto"
                                            @click="resolveDialog(false)"
                                        />
                                    </template>

                                    <BaseButton
                                        v-else
                                        type="button"
                                        :label="dialog.actionText"
                                        :variant="dialog.variant === DialogVariants.Danger ? 'danger' : 'primary'"
                                        :icon="dialog.actionIcon"
                                        :loading="dialog.loading"
                                        :disabled="dialog.loading"
                                        class="w-full"
                                        @click="resolveDialog(true)"
                                    />
                                </div>
                            </DialogPanel>
                        </TransitionChild>
                    </div>
                </div>
            </DialogParent>
        </TransitionRoot>
    </Teleport>
</template>
